import { InputHTMLAttributes } from "react"
import { Form, InputGroup } from "react-bootstrap"
import classNames from "classnames"
import { FieldErrors, Control } from "react-hook-form"
import { useToggle } from "hooks"
import "./index.css"

type PasswordInputProps = {
  name: string
  id?: string
  placeholder?: string
  refCallback?: any
  errors: FieldErrors
  control?: Control<any>
  register?: any
  className?: string
}

/* Password Input */
const PasswordInput = ({
  name,
  id,
  placeholder,
  refCallback,
  errors,
  register,
  className,
  ...otherProps
}: PasswordInputProps) => {
  const [showPassword, togglePassword] = useToggle()

  return (
    <InputGroup className="mb-0">
      <Form.Control
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        name={name}
        id={id ?? name}
        as="input"
        ref={(r: HTMLInputElement) => {
          if (refCallback) refCallback(r)
        }}
        className={className}
        isInvalid={errors && errors[name] ? true : false}
        {...(register ? register(name) : {})}
        autoComplete={name}
        {...otherProps}
      />
      <button
        type="button"
        className={classNames(
          "input-group-text cursor-pointer",
          "input-group-password",
          {
            "show-password": showPassword,
          }
        )}
        data-password={showPassword ? "true" : "false"}
        onClick={togglePassword}
        tabIndex={-1}
      >
        <span className="password-eye"></span>
      </button>
    </InputGroup>
  )
}

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  type?: string
  name: string
  placeholder?: string
  register?: any
  errors?: FieldErrors
  control?: Control<any>
  className?: string
  labelClassName?: string
  containerClass?: string
  refCallback?: any
  children?: React.ReactNode
  rows?: string
  inputmode?: string
  warnings?: any
  rightText?: string
}

const FormInput = ({
  label,
  type,
  name,
  placeholder,
  register,
  errors,
  control,
  className,
  labelClassName,
  containerClass,
  refCallback,
  children,
  pattern,
  inputmode,
  rows,
  warnings,
  rightText,
  ...otherProps
}: FormInputProps) => {
  // handle input type
  const comp =
    type === "textarea" ? "textarea" : type === "select" ? "select" : "input"

  if (type === "hidden")
    return (
      <input
        type={type}
        name={name}
        {...(register ? register(name) : {})}
        {...otherProps}
      />
    )

  if (type === "password")
    return (
      <Form.Group className={containerClass}>
        {label ? (
          <>
            {" "}
            <Form.Label className={labelClassName}>{label}</Form.Label>{" "}
            {children}{" "}
          </>
        ) : null}
        <PasswordInput
          name={name}
          placeholder={placeholder}
          refCallback={refCallback}
          errors={errors!}
          register={register}
          className={className}
          {...otherProps}
        />

        {errors && errors[name] ? (
          <Form.Control.Feedback type="invalid" className="d-block">
            {/*{errors[name]['message']}*/}
          </Form.Control.Feedback>
        ) : null}
      </Form.Group>
    )

  if (type === "select")
    return (
      <Form.Group className={containerClass}>
        {label ? (
          <Form.Label className={labelClassName}>{label}</Form.Label>
        ) : null}

        <Form.Select
          type={type}
          label={label}
          name={name}
          id={name}
          ref={(r: HTMLInputElement) => {
            if (refCallback) refCallback(r)
          }}
          comp={comp}
          className={className}
          isInvalid={errors && errors[name] ? true : false}
          {...(register ? register(name) : {})}
          {...otherProps}
        >
          {children}
        </Form.Select>

        {errors && errors[name] ? (
          <Form.Control.Feedback type="invalid">
            {/*{errors[name]['message']}*/}
          </Form.Control.Feedback>
        ) : null}
      </Form.Group>
    )

  if (type === "checkbox" || type === "radio")
    return (
      <Form.Group className={containerClass}>
        <Form.Check
          type={type}
          label={label}
          name={name}
          id={name}
          ref={(r: HTMLInputElement) => {
            if (refCallback) refCallback(r)
          }}
          className={className}
          isInvalid={errors && errors[name] ? true : false}
          {...(register ? register(name) : {})}
          {...otherProps}
        />

        {errors && errors[name] ? (
          <Form.Control.Feedback type="invalid">
            {/*{errors[name]['message']}*/}
          </Form.Control.Feedback>
        ) : null}
      </Form.Group>
    )
  return (
    <Form.Group className={containerClass}>
      {label ? (
        <Form.Label className={labelClassName}>{label}</Form.Label>
      ) : null}
      <div className="relative">
        <Form.Control
          type={type}
          placeholder={placeholder}
          pattern={pattern}
          inputMode={inputmode}
          name={name}
          id={name}
          as={comp}
          ref={(r: HTMLInputElement) => {
            if (refCallback) refCallback(r)
          }}
          className={className}
          isInvalid={errors && errors[name] && !warnings ? true : false}
          {...(register ? register(name) : {})}
          {...otherProps}
          autoComplete={name}
        >
          {children ? children : null}
        </Form.Control>

        {rightText && (
          <span className="absolute bottom-2 right-2">{rightText}</span>
        )}
      </div>
      {/* {errors && errors[name] ? (
    <Form.Control.Feedback type="invalid">

    </Form.Control.Feedback>
  ) : null} */}

      {warnings && !errors?.[name] && (
        <i className="ri ri-error-warning-line absolute rounded-full  text-yellow-500 text-lg flex items-center justify-center right-2 top-1" />
      )}
      {/* {errors && (
        <i className='ri ri-error-warning-line absolute rounded-full  text-red-500 text-lg flex items-center justify-center right-2 top-1' />
      )} */}
    </Form.Group>
  )
}

export default FormInput
